import styled from "styled-components"

import { TeamsIcon } from "@minuthq/meatball-ui-icons"
import {
  cardBoxShadow,
  colors,
  ListBox,
  Popover,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { RoundProfilePicture } from "src/components/Account/Avatars"
import { getProfilePopoverRoot } from "src/components/AppLayout/navUtils"
import { ErrorBoundary } from "src/components/ErrorBoundary/ErrorBoundary"
import { useAuthState } from "src/context/auth/useAuthState"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { isSuperAdmin } from "src/data/user/logic/accessLogic"
import { useUserCache } from "src/data/user/queries/userQueryCache"
import { useLogout } from "src/hooks/useLogout"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { MButton } from "src/ui/Button/MButton"
import { Divider } from "src/ui/Divider/Divider"
import CopyIcon from "src/ui/icons/copy.svg"
import Chevron from "src/ui/icons/expand-down.svg"
import ProfileIcon from "src/ui/icons/profile.svg"
import { localStorageFactory } from "src/utils/storageUtil"

import {
  Fullname,
  ProfileInfo,
  ProfileInfoCenter,
  ProfileInfoCenterTitle,
  ProfileListItem,
} from "./sharedStyles"

export function ParadiseProfileButton({
  showLabel = true,
  clickCb = () => {},
}: {
  showLabel?: boolean
  clickCb?: () => void
}) {
  const { t, langKeys } = useTranslate()

  const user = useGetUser()
  const authState = useAuthState()
  const { invalidateUserFuzzy } = useUserCache()
  const fakeSalesAdminStorage = localStorageFactory<{ isSalesAdmin: boolean }>({
    key: "minut.paradise.user.faking_sales_admin",
  })

  const { navigate } = useRouter()

  const { logout } = useLogout()

  const copyAcessToken = () => {
    if (authState.authorization) {
      navigator.clipboard.writeText(authState.authorization.access_token)
    }
  }

  return (
    <ErrorBoundary mini>
      <Popover
        placement="bottom"
        trigger={
          <MButton
            aria-controls="profile-menu"
            aria-haspopup="true"
            variant="minimal"
            fullWidth={showLabel}
            color="contrast"
          >
            <ProfileInfo>
              <RoundProfilePicture />
              {showLabel && (
                <ProfileInfoCenter>
                  <ProfileInfoCenterTitle>
                    <Fullname>{user.fullname}</Fullname>
                    <Chevron fill={colors.textSecondary} width={10} />
                  </ProfileInfoCenterTitle>
                </ProfileInfoCenter>
              )}
            </ProfileInfo>
          </MButton>
        }
        root={getProfilePopoverRoot()}
      >
        <PopoverContent>
          <ProfileListItem>
            <div>
              <RoundProfilePicture />
            </div>
            <ProfileInfoCenter>
              <Text variant="body" color="secondary">
                {user.fullname}
              </Text>
              <Text variant="bodyS" color="secondary">
                {user.email}
              </Text>
            </ProfileInfoCenter>
          </ProfileListItem>

          <Divider />

          <ListBox
            items={[
              {
                id: "account",
                prefix: <ProfileIcon width="24" height="24" />,
                content: t(langKeys.account),
                onClick: () => {
                  clickCb()
                  navigate(Routes.AccountGeneral.location())
                },
              },
              {
                id: "copy-access-token",
                prefix: <CopyIcon width="24" height="24" />,
                content: "Copy access token",
                onClick: () => {
                  clickCb()
                  copyAcessToken()
                },
              },
              {
                id: "become-sales-admin",
                prefix: <TeamsIcon width="24" height="24" />,
                content: isSuperAdmin(user.roles)
                  ? "Become sales admin"
                  : "Become superadmin",
                onClick: () => {
                  if (isSuperAdmin(user.roles)) {
                    // write is isSalesAdmin in local storage
                    fakeSalesAdminStorage.set({
                      isSalesAdmin: true,
                    })
                  } else {
                    fakeSalesAdminStorage.set({
                      isSalesAdmin: false,
                    })
                  }
                  // Invalidate the user cache
                  invalidateUserFuzzy(user.user_id)

                  // Since the getUser isn't reactive, we need to reload the page to get the new user
                  window.location.reload()
                },
                disabled: !isSuperAdmin(user.roles),
              },
            ]}
          />

          <Divider />

          <ListBox
            items={[
              {
                id: "logout",
                content: t(langKeys.sign_out),
                onClick: () => {
                  logout({
                    reason: "User clicked logout in side nav",
                    userInitiated: true,
                  })
                },
              },
            ]}
          />
        </PopoverContent>
      </Popover>
    </ErrorBoundary>
  )
}

const PopoverContent = styled.div`
  background-color: ${colors.neutralLight};
  border-radius: 1rem;
  padding-block: ${spacing.XS};
  ${cardBoxShadow}
`
